import React, { ChangeEvent, useEffect, useState } from "react";
import CustomButton from "components/Button";
import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import Upload from "assets/icons/Upload";
import CustomSelectComponent from "components/Select";
import CustomInput from "components/Input";
import Search from "assets/icons/Search";
import OurApp from "assets/icons/OurApp";
import SaveTab from "assets/icons/SaveTab";
import OwnPicture from "./OwnPicture";
import useVerticals from "mokcup/verticals";
import CustomTabs from "components/PWATabs/CustomTabs";
import useFetch from "hooks/useFetch";
import { useAppStore } from "store/appStore";
import { ICreative, useCreativesUplodStore } from "store/Creatives";
import showPreviewIcon from "utils/showPreviewImage";
import useFetchFormData from "hooks/useFetchFormData";
import { AppDataMockup } from "interfaces/mockupApp";
import useSetupStore from "store/setupSettingsStore";
import { useAuthStore } from "store/authStore";
import Save from "assets/icons/Save";
import ToastMessage from "components/ToastMessage";
import "styles/toastStyles.css";
import "react-toastify/dist/ReactToastify.css";
import SwitchComponent from "components/Checkbox";
import { getToken } from "utils/getToken";
import { LazyImage } from "../../LazyImage";
import imgPlaceholder from "assets/imageApp.png";
import useMediaByValue from "hooks/useMediaByValue";
import { BootstrapTooltip } from "../mainTab/CommentsTable";
import TouchAppIcon from "@mui/icons-material/TouchApp";

export default function CreativesTab({ setValue }: { setValue: (value: number) => void }) {
  const { appData, saveAppData } = useAppStore();
  const [valueCreatives, setValueCreatives] = useState(2);
  const [images, setImages] = useState<ICreative[]>([]);
  const [ourSuggestions, setOurSuggestion] = useState<ICreative[]>([]);
  const [openOPModal, setOPModal] = useState(false);
  const { t } = useTranslation();
  const verticals = useVerticals();
  const token = getToken();

  const { creativesUpload } = useCreativesUplodStore();
  const { user, setUser } = useAuthStore();

  const [searchValue, setSearchValue] = useState("");
  const [creativesData, setCreativesData] = useState([]);
  const [savedCreatives, setSavedCreatives] = useState<ICreative[]>(user?.creatives || []);
  const [hoveredCreativesIndex, setHoveredCreativesIndex] = useState<number | null>(null);
  const isTabletPlus = useMediaByValue("(min-width:1000px)");

  const setStepStatus = useSetupStore((state) => state.setStepStatus);

  useEffect(() => {
    setStepStatus("creatives", "inprogress");
    return () => {
      setStepStatus("creatives", "ready");
    };
  }, [setStepStatus]);

  const { triggerFetch: creativesGroup } = useFetch({
    url: "creatives/groups",
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const { triggerFetch: addedUserCreatives, error: addUserCreativesError } = useFetchFormData({
    url: "profile/added-user-creatives",
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const { triggerFetch: deleteUserCreatives } = useFetchFormData({
    url: "profile/delete-user-creatives",
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const { triggerFetch: getUserInfo } = useFetch({
    url: "profile/user-info",
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const userInfo = async () => {
    const res = await getUserInfo();
    if (res.data) {
      localStorage.setItem("userData", JSON.stringify(res.data));
      setUser(res.data);
    }
  };
  const tabs = [
    {
      icon: <Upload color={valueCreatives === 0 ? "#F4701B" : "#6D6777"} />,
      label: t("myUploads"),
    },
    {
      icon: <SaveTab color={valueCreatives === 1 ? "#F4701B" : "#6D6777"} />,
      label: t("saved"),
    },
    {
      icon: <OurApp color={valueCreatives === 2 ? "#F4701B" : "#6D6777"} />,
      label: t("ourSuggestions"),
    },
  ];

  useEffect(() => {
    if (valueCreatives === 0) {
      setImages(creativesUpload);
    } else if (valueCreatives === 1) {
      setImages(savedCreatives);
    } else if (valueCreatives === 2) {
      setImages(ourSuggestions);
    }
  }, [valueCreatives, creativesUpload, ourSuggestions, savedCreatives]);

  useEffect(() => {
    getCreatives();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appData?.categoryVertical]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearchValue(value);

    if (value.trim() === "") {
      const filteredSuggestions = creativesData
        .filter((suggestion: any) => {
          const selectedVertical = verticals.find((vertical) => vertical.value === appData?.categoryVertical);
          return selectedVertical ? suggestion.group_type === selectedVertical.id : false;
        })
        .map((suggestion: any) => ({
          icon: suggestion.images.icon || null,
          previews: suggestion.images.previews || [],
          isAnimated: suggestion.isAnimated || false,
          animated_icon: suggestion.animated_images.icon || null,
          animated_previews: suggestion.animated_images.previews || [],
        }));

      setOurSuggestion(filteredSuggestions.reverse() || []);
      return;
    }

    const filteredSuggestions = creativesData.filter((item: any) =>
      item.group_tags.some((tag: string) => tag.toLowerCase().includes(value))
    );

    const suggestions = filteredSuggestions.map((suggestion: any) => ({
      icon: suggestion.images?.icon || null,
      previews: suggestion.images?.previews || [],
      isAnimated: suggestion.isAnimated || false,
      animated_icon: suggestion.animated_images.icon || null,
      animated_previews: suggestion.animated_images.previews || [],
    }));

    setOurSuggestion(suggestions.reverse() || []);
  };

  async function getCreatives() {
    const result = await creativesGroup();

    setCreativesData(result.data);

    const filteredSuggestions = result.data
      .filter((suggestion: any) => {
        const selectedVertical = verticals.find((vertical) => vertical.value === appData?.categoryVertical);

        return selectedVertical ? suggestion.group_type === selectedVertical.id : "gambling";
      })
      .map((suggestion: any) => ({
        icon: suggestion.images.icon || null,
        previews: suggestion.images.previews || [],
        isAnimated: suggestion.isAnimated || false,
        animated_icon: suggestion.animated_images.icon || null,
        animated_previews: suggestion.animated_images.previews || [],
      }));

    setOurSuggestion(filteredSuggestions.reverse() || []);
  }

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof AppDataMockup
  ) => {
    const { value } = event.target;
    saveAppData({ [field]: value });
  };

  const handleSelectChange = (event: ChangeEvent<{ value: unknown }>, field: keyof AppDataMockup) => {
    const { value } = event.target;

    saveAppData({ [field]: value as string });
  };

  const handleCheckboxChange = (event: any, field: keyof AppDataMockup) => {
    const { checked } = event.target;
    saveAppData({ [field]: checked });
  };

  const handleUpdatePwa = async () => {
    setValue(2);
    localStorage.setItem("lastTabValue", "2");
  };

  const handleSaveCreative = (imageSet: ICreative) => {
    saveAppData({
      creatives: {
        ...appData.creatives,
        icon: imageSet.icon,
        previews: imageSet.previews,
      },
    });
  };

  const handleUserCreative = async (creative: ICreative) => {
    const isAlreadySaved = savedCreatives.some((item) => item.icon === creative.icon);

    if (isAlreadySaved) {
      const data = new FormData();
      if (typeof creative.icon === "string") {
        data.append("creativeNameToRemove", creative.icon);
        const response = await deleteUserCreatives(data);
        if (response.success) {
          userInfo();
          setSavedCreatives(savedCreatives.filter((item) => item.icon !== creative.icon));
        } else {
          ToastMessage({ mode: "error", message: response.message });
        }
      }
    } else {
      const data = new FormData();
      if (creative.icon) {
        data.append("icon", creative.icon);
      }
      if (creative.previews && creative.previews.length > 0) {
        creative.previews.forEach((preview) => {
          data.append("previews", preview);
        });
      }
      setSavedCreatives([...savedCreatives, creative]);
      try {
        const res = await addedUserCreatives(data);
        if (res) {
          userInfo();
          setSavedCreatives([...savedCreatives, creative]);
        }
        console.log("Ошибка при добавлении creative:", addUserCreativesError);
        if (addUserCreativesError) {
          ToastMessage({ mode: "error", message: addUserCreativesError });
          console.log("Ошибка при добавлении creative:", addUserCreativesError);
        }
      } catch (error) {
        console.error("Ошибка при добавлении creative:", error);
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          border: "1px solid #EEEEEE",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          padding: { xs: "16px", md: "24px" },
        }}
      >
        <Box
          sx={{
            maxWidth: "460px",
          }}
        >
          <Typography
            sx={{
              marginBottom: { xs: "12px", md: "16px" },
              fontFamily: "var(--cygreMedium), sans-serif",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: 1.35,
              color: "#2E263D",
            }}
          >
            {t("creatives")}
          </Typography>
          <Typography
            sx={{
              marginBottom: "24px",
              fontFamily: "var(--cygreRegular), sans-serif",
              color: "#6D6777",
              fontSize: "14px",
              lineHeight: 1.35,
            }}
          >
            {t("selectsetimages")}
          </Typography>
          <Box
            sx={{
              width: { xs: "100%", sm: "186px" },
              marginBottom: { xs: "16px", md: "44px" },
            }}
          >
            <CustomButton
              startIconSrc={
                <Box sx={{ marginRight: "8px" }}>
                  <Upload color={"#6D6777"} />
                </Box>
              }
              width="100%"
              customPadding="8px 16px 8px 12px"
              textButton={t("downloadown")}
              customTextTransform="none"
              customBgColor="transparent"
              customHoverColor="transparent"
              customBorderColor="#C9D0D8"
              customColor="#6D6777"
              onClick={() => setOPModal(true)}
              customBorderRadius="8px"
              height={40}
              customFontSize="15px"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              marginBottom: "16px",
            }}
          >
            <CustomSelectComponent
              title={t("category")}
              options={verticals}
              backgroundColor="#F6F7F9"
              noBorder={true}
              value={appData.categoryVertical}
              onChange={(e: any) => handleSelectChange(e, "categoryVertical")}
            />
            <CustomInput
              required
              fullWidth
              id="youtubeVideoLink"
              height="40px"
              placeholder={t("enterLinkOrFrame")}
              name="rating"
              label={t("youTubevideo")}
              value={appData!.youtubeVideoLink}
              onChange={(e) => handleInputChange(e, "youtubeVideoLink")}
              customBgColor="#F6F7F9"
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                lineHeight: "1.43",
                color: "#2E263D",
              }}
            >
              {t("videoSound")}
            </Typography>
            <SwitchComponent
              checked={appData!.soundVideo}
              onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
                handleCheckboxChange(e, "soundVideo")
              }
            />
          </Box>
        </Box>
        <Box
          sx={{
            borderTop: "1px solid #EEEEEE",
            marginTop: { xs: "16px", md: "24px" },
            paddingTop: { xs: "16px", md: "24px" },
          }}
        >
          <CustomInput
            required
            fullWidth
            id="search"
            height="40px"
            placeholder={t("contentSearch")}
            name="search"
            value={searchValue}
            onChange={handleSearchChange}
            customBgColor="#F6F7F9"
            iconEnd={<Search />}
          />
          <Box
            sx={{
              marginTop: "12px",
              marginBottom: { xs: "16px", md: "24px" },
              maxWidth: "100%",
              padding: "0px 8px",
              height: "48px",
              borderRadius: "8px",
              background: "#F6F7FB",
            }}
          >
            <CustomTabs
              width="100%"
              customTabWidth={isTabletPlus ? "calc((100% - 8px) / 3)" : "auto"}
              value={valueCreatives}
              setValue={setValueCreatives}
              tabs={tabs}
              t={t}
            />
          </Box>
          <Grid
            container
            spacing={1}
            sx={{
              maxWidth: "100%",
            }}
          >
            {images &&
              images.map((imageSet: any, index: number) => {
                const isCreativesAnimated = imageSet.isAnimated;
                const staticCreatives = { icon: imageSet.icon, previews: imageSet.previews };
                const animatedCreatives = { icon: imageSet.animated_icon, previews: imageSet.animated_previews };

                const showAnimatedCreatives = isCreativesAnimated && hoveredCreativesIndex === index;
                const iconSrc = showAnimatedCreatives ? imageSet.animated_icon : imageSet.icon;
                const previewsSrc = showAnimatedCreatives ? imageSet.animated_previews : imageSet.previews;
                const isCurrentCreativeClicked =
                  appData.creatives.icon === imageSet.icon || appData.creatives.icon === imageSet.animated_icon;

                const handleCreativesCardClick = () => {
                  if (isCreativesAnimated) {
                    setHoveredCreativesIndex(index);
                  } else {
                    setHoveredCreativesIndex(null);
                  }
                  handleSaveCreative(isCreativesAnimated ? animatedCreatives : staticCreatives);
                };

                return (
                  <Grid size={{ xs: 12, lg: 12 / 2, xl: 12 / 3 }}>
                    <Box
                      key={index}
                      onClick={() => handleCreativesCardClick()}
                      onMouseEnter={() => setHoveredCreativesIndex(index)}
                      onMouseLeave={() => setHoveredCreativesIndex(null)}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        padding: "10px 8px 8px 8px",
                        border: `1px solid ${isCurrentCreativeClicked ? "#F4701B" : "#EEEEEE"}`,
                        borderRadius: "8px",
                        backgroundColor: isCurrentCreativeClicked ? "#F6F7FB" : "#fff",
                        cursor: "pointer",
                        contain: "inline-size",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "8px",
                        }}
                      >
                        <img
                          style={{
                            width: "100%",
                            height: "auto",
                            maxWidth: "70px",
                            maxHeight: "70px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            aspectRatio: "1",
                          }}
                          src={showPreviewIcon(iconSrc)}
                          alt="icon"
                        />
                        {isCreativesAnimated && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              height: "fit-content",
                              padding: "4px 8px",
                              border: "1px solid #EEEEEE",
                              borderRadius: "8px",
                            }}
                          >
                            <Typography
                              sx={{
                                paddingTop: "4px",
                                fontFamily: "var(--cygreMedium), sans-serif",
                                color: "#2E263D",
                                fontSize: "12px",
                                lineHeight: 1.22,
                                textAlign: "left",
                              }}
                            >
                              {t("animated")}
                            </Typography>

                            <TouchAppIcon sx={{ width: "22px", height: "22px", color: "#F4701B" }} />
                          </Box>
                        )}
                        <BootstrapTooltip title={t("save")} placement="top">
                          <Box
                            onClick={(event) => {
                              event.stopPropagation();
                              handleUserCreative(imageSet);
                            }}
                          >
                            <Save
                              color={savedCreatives.some((item) => item.icon === imageSet.icon) ? "#F4701B" : "#fff"}
                              colorStroke={
                                savedCreatives.some((item) => item.icon === imageSet.icon) ? "#F4701B" : "#6D6777"
                              }
                            />
                          </Box>
                        </BootstrapTooltip>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: "4px",
                          overflowX: "auto",
                          paddingBottom: "2px",
                        }}
                      >
                        {previewsSrc &&
                          previewsSrc.map((preview: any, index: number) => {
                            return (
                              <LazyImage
                                key={index}
                                customStyles={{
                                  objectFit: "contain",
                                  maxHeight: "150px",
                                  borderRadius: "4px",
                                }}
                                src={showPreviewIcon(preview)}
                                placeholder={imgPlaceholder}
                                alt="preview"
                              />
                            );
                          })}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
          </Grid>
        </Box>
        <Box sx={{ width: "160px", marginTop: "16px" }}>
          <CustomButton
            textButton={t("next")}
            height={40}
            customBgColor="#F4701B"
            customHoverColor="#F4701B"
            customBorderRadius="8px"
            customFontSize="14px"
            onClick={handleUpdatePwa}
          />
        </Box>
      </Box>
      <OwnPicture open={openOPModal} setOpen={setOPModal} handleSaveCreative={handleSaveCreative} />
    </>
  );
}
